// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
import MDBadge from "theme/components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

function Organisation() {
  const pageTitle = "Organisation";
  usePageTitle(pageTitle);

  const { id: reference } = useParams();
  const navigate = useNavigate();

  const [refreshPage, setRefreshPage] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [organisation, setOrganisation] = useState({});
  const [staffNumber, setStaffNumber] = useState(null);
  const [licence, setLicence] = useState(null);
  const [teams, setTeams] = useState([]);

  const [openStaffNumberDialog, setOpenStaffNumberDialog] = useState(false);

  const tableTeamsColumns = [
    { Header: "Team", accessor: "name", align: "left" },
    { Header: "Actions", accessor: "action", align: "left" },
  ];

  const buildTeamsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        action: (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            iconOnly
            circular
            onClick={() => {
              navigate(`/organisations/teams/${row.reference}`);
            }}
          >
            <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
              chevron_right
            </Icon>
          </MDButton>
        ),
        name: row.name,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`agents/teams/organisations/${reference}/fetch.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setOrganisation(result.data.data.organisation);
            setStaffNumber(result.data.data.staff_number);
            setLicence(result.data.data.license);
            const rows = buildTeamsRows(result.data.data.teams);
            setTeams(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function saveStaffNumber() {
    setOpenStaffNumberDialog(false);
    setShowLoading(true);

    const json = {
      reference: `${reference}`,
      staff_number: staffNumber,
    };

    await axios
      .post("agents/teams/organisations/staffNumber.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);
          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openStaffNumberDialog}>
        <DialogTitle>
          <MDBox>Staff Number</MDBox>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <MDBox>Enter your company staff number.</MDBox>
          </DialogContentText>
          <MDBox mt={2}>
            <MDInput
              name="staff_number"
              margin="dense"
              label="Staff Number"
              type="text"
              fullWidth
              variant="outlined"
              onChange={(e) => setStaffNumber(e.target.value)}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenStaffNumberDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Close
          </MDButton>
          <MDButton
            onClick={() => {
              saveStaffNumber();
            }}
            color="success"
            variant="outlined"
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox pt={3} pb={3}>
        <Card sx={{ width: "100%", marginTop: "15px" }}>
          <MDBox p={3}>
            <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
              <img src={`${AppConfig.api_media_url}${organisation.icon}`} alt="" width="18px" />{" "}
              &nbsp;
              {organisation.name}
            </MDTypography>
            <MDTypography variant="button" fontWeight="light" gutterBottom>
              You are a member of the {organisation.name} sales team.
            </MDTypography>
          </MDBox>
        </Card>
      </MDBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={6}>
          <MDBox pt={3} pb={3}>
            <Card sx={{ width: "100%" }}>
              <MDBox p={5} align="center">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={12} lg={12}>
                    <MDBox align="center">
                      <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                        Staff Number:
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12}>
                    <MDBox align="center">
                      <MDTypography variant="h6" fontWeight="light" textTransform="capitalize">
                        {staffNumber}
                        &nbsp;&nbsp;
                        <MDButton
                          size="small"
                          variant="outlined"
                          color="warning"
                          iconOnly
                          onClick={() => {
                            setOpenStaffNumberDialog(true);
                          }}
                        >
                          <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                            edit
                          </Icon>
                        </MDButton>
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          <MDBox pt={3} pb={2}>
            <Card sx={{ width: "100%" }}>
              <MDBox p={2.5}>
                {licence ? (
                  <MDBox align="center">
                    <MDTypography variant="h6" fontWeight="medium">
                      Subscription
                    </MDTypography>
                    <MDTypography variant="h6" fontWeight="light" mt={1}>
                      <MDBadge
                        badgeContent={licence.status ? "ACTIVE" : "EXPIRED"}
                        variant="contained"
                        container
                        color={licence.status ? "success" : "error"}
                      />
                    </MDTypography>
                    <MDBox mt={1}>
                      <MDTypography variant="h6" fontWeight="light">
                        Expires On
                        <br />
                        <b>{licence.expires_on}</b>
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                ) : (
                  <MDBox mb={3}>
                    <MDTypography variant="h6" fontWeight="light">
                      No active subscription.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </MDBox>
        </Grid>
      </Grid>

      <MDBox pt={3} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox>
                  <Card sx={{ height: "100%", marginTop: "0px" }}>
                    <MDBox p={3}>
                      <MDBox ml={2} mr={3}>
                        <Grid container spacing={3}>
                          <Grid item xs={6} md={6} lg={6}>
                            <MDTypography variant="h6" fontWeight="medium">
                              Teams
                            </MDTypography>
                          </Grid>
                        </Grid>
                      </MDBox>
                      <MDBox mt={3}>
                        <DataTable
                          table={{ columns: tableTeamsColumns, rows: teams }}
                          isSorted
                          canSearch={false}
                          entriesPerPage={false}
                          showTotalEntries
                          noEndBorder
                        />
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Organisation;
